/**
 * Sales Summary Item Model - Encapsulates individual sales item data
 */
class SalesSummaryItemModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  // ===== SERVER DATA PROPERTIES =====

  get qty_sold() {
    return this._serverData?.qty_sold ?? 0;
  }

  get item_price() {
    return this._serverData?.item_price ?? 0;
  }

  get total_sold_amount() {
    return this._serverData?.total_sold_amount ?? 0;
  }

  get date_time() {
    return this._serverData?.date_time ?? '';
  }

  // ===== FORMATTED PROPERTIES =====

  get formattedQty() {
    return this.qty_sold;
  }

  get formattedItemPrice() {
    return this._formatCurrency(this.item_price);
  }

  get formattedPriceSold() {
    return this._formatCurrency(this.total_sold_amount);
  }

  get formattedDateTime() {
    return this._formatDateTime(this.date_time);
  }

  // ===== UI FORMAT (for table rows) =====

  toRowFormat() {
    return {
      qty: this.qty_sold,
      itemPrice: this.item_price,
      priceSold: this.total_sold_amount,
      dateTime: this.formattedDateTime,
    };
  }

  // ===== HELPER METHODS =====

  _formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0.00';
    }
    return Number(value).toFixed(2);
  }

  _formatDateTime(dateString: string): string {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${month}/${day}/${year} ${displayHours}:${minutes}:${seconds} ${ampm}`;
  }

  // ===== UTILITY METHODS =====

  getRawData() {
    return this._serverData;
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new SalesSummaryItemModel(serverData);
  }

  static createEmpty() {
    return new SalesSummaryItemModel();
  }
}

/**
 * Sales Summary Model - Encapsulates sales summary data and provides clean interface for UI components
 * This model handles data transformation, validation, and formatted properties
 */
class SalesSummaryModel {
  private _summaryData: any;
  private _salesItems: SalesSummaryItemModel[];

  constructor(summaryData: any = null, salesItems: any[] = []) {
    this._summaryData = summaryData;
    this._salesItems = salesItems.map(item => SalesSummaryItemModel.fromServerData(item));
  }

  // ===== SERVER DATA PROPERTIES =====

  get total_sold_quantity() {
    return this._summaryData?.total_sold_quantity ?? 0;
  }

  get total_gross_sales() {
    return this._summaryData?.total_gross_sales ?? 0;
  }

  get invoice_margin_percentage() {
    return this._summaryData?.invoice_margin_percentage ?? 0;
  }

  get total_profit() {
    return this._summaryData?.total_profit ?? 0;
  }

  get average_sales_per_day() {
    return this._summaryData?.average_sales_per_day ?? 0;
  }

  get average_sales_per_week() {
    return this._summaryData?.average_sales_per_week ?? 0;
  }

  // ===== FORMATTED PROPERTIES =====

  get formattedTotalItemsSold() {
    return this.total_sold_quantity;
  }

  get formattedTotalGrossSales() {
    return this._formatCurrency(this.total_gross_sales);
  }

  get formattedInvoiceMargin() {
    return this._formatPercentage(this.invoice_margin_percentage);
  }

  get formattedTotalProfits() {
    return this._formatCurrency(this.total_profit);
  }

  get formattedAvgSalesPerDay() {
    return this.average_sales_per_day;
  }

  get formattedAvgSalesPerWeek() {
    return this.average_sales_per_week;
  }

  // ===== COLLECTIONS =====

  get sales() {
    return this._salesItems;
  }

  // ===== UI FORMAT (for backward compatibility) =====

  toSummaryFormat() {
    return {
      totalItemsSold: this.total_sold_quantity,
      totalGrossSales: this.total_gross_sales,
      invoiceMargin: this.invoice_margin_percentage,
      totalProfits: this.total_profit,
      avgSalesPerDay: this.average_sales_per_day,
      avgSalesPerWeek: this.average_sales_per_week,
    };
  }

  toSalesFormat() {
    return this._salesItems.map(item => item.toRowFormat());
  }

  // ===== HELPER METHODS =====

  _formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0.00';
    }
    return Number(value).toFixed(2);
  }

  _formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0.00';
    }
    return Number(value).toFixed(2);
  }

  // ===== UTILITY METHODS =====

  getRawData() {
    return {
      summary: this._summaryData,
      sales: this._salesItems.map(item => item.getRawData()),
    };
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    if (!serverData) {
      return new SalesSummaryModel();
    }
    return new SalesSummaryModel(serverData.summary, serverData.sales || []);
  }

  static createEmpty() {
    return new SalesSummaryModel();
  }
}

export default SalesSummaryModel;
export { SalesSummaryItemModel };

